import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useTheme } from "../contexts/ThemeContext";
import { Button } from "./ui/button";
import { Moon, Sun, LogOut, Menu } from "lucide-react";
import { useState } from "react";
import PendingBanner from "./PendingBanner";
import {
  Sheet, SheetContent, SheetTrigger,
} from "./ui/sheet";

export default function Header() {
  const { user, logout } = useAuth();
  const { theme, toggle } = useTheme();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  const isAuthed = user && typeof user === "object";

  const publicLinks = [
    { to: "/#planos", label: "Planos" },
    { to: "/beneficios", label: "Benefícios" },
    { to: "/parceiros", label: "Parceiros" },
    { to: "/#matriz", label: "Matriz" },
    { to: "/#pre-market", label: "Pré-lançamento" },
  ];

  const isBuyer = isAuthed && user.account_type === "buyer";
  const authedLinks = [
    { to: "/dashboard", label: "Escritório" },
    ...(isBuyer ? [] : [
      { to: "/matriz", label: "Matriz" },
      { to: "/indicacoes", label: "Indicações" },
      { to: "/relatorio", label: "Relatório" },
      { to: "/saque", label: "Saque" },
    ]),
    { to: "/transferir", label: "Transferir" },
    { to: "/pedidos", label: "Pedidos" },
    { to: "/beneficios", label: "Benefícios" },
    { to: "/parceiros", label: "Parceiros" },
    { to: "/perfil", label: "Perfil" },
    ...(isAuthed && user.role === "admin" ? [{ to: "/admin", label: "Admin" }] : []),
  ];

  const links = isAuthed ? authedLinks : publicLinks;

  const NavContent = () => (
    <>
      {links.map((l) => (
        <a
          key={l.to}
          href={l.to}
          onClick={(e) => {
            if (l.to.startsWith("/#")) {
              e.preventDefault();
              const id = l.to.slice(2);
              const el = document.getElementById(id);
              if (el) el.scrollIntoView({ behavior: "smooth" });
              setOpen(false);
            } else {
              e.preventDefault();
              navigate(l.to);
              setOpen(false);
            }
          }}
          className="text-sm font-medium text-muted-foreground hover:text-foreground transition-colors"
          data-testid={`nav-${l.label.toLowerCase().replace(/\s/g, "-")}`}
        >
          {l.label}
        </a>
      ))}
    </>
  );

  return (
    <header className="fixed top-0 inset-x-0 z-50 border-b border-border/60 glass">
      <div className="max-w-7xl mx-auto flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
        <Link to="/" className="flex items-center gap-3" data-testid="brand-link">
          <img
            src="/media/logo.jpg"
            alt="Conecta Mais"
            className="h-10 sm:h-11 w-auto rounded-md"
            data-testid="brand-logo"
          />
          <div className="hidden sm:block xl:hidden leading-none">
            <div className="text-[10px] uppercase tracking-[0.3em] text-muted-foreground">participação · benefícios</div>
          </div>
        </Link>

        <nav className="hidden md:flex items-center gap-8">
          <NavContent />
        </nav>

        <div className="flex items-center gap-2">
          <Button variant="ghost" size="icon" onClick={toggle} aria-label="Alternar tema" data-testid="theme-toggle">
            {theme === "dark" ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </Button>

          {isAuthed ? (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/dashboard")}
                data-testid="header-dashboard-btn"
                className="hidden sm:inline-flex"
              >
                {user.name?.split(" ")[0]}
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => { logout(); navigate("/"); }}
                data-testid="logout-btn"
              >
                <LogOut className="h-4 w-4 sm:mr-2" /><span className="hidden sm:inline">Sair</span>
              </Button>
            </>
          ) : (
            <>
              <Button
                variant="ghost"
                onClick={() => navigate("/login")}
                data-testid="header-login-btn"
                className="hidden sm:inline-flex"
              >
                Entrar
              </Button>
              <Button
                onClick={() => navigate("/cadastro")}
                data-testid="header-register-btn"
                className="gold-btn hover:opacity-95"
              >
                Criar conta
              </Button>
            </>
          )}

          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="md:hidden" data-testid="mobile-menu-btn">
                <Menu className="h-5 w-5" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-72">
              <div className="mt-8 flex flex-col gap-4">
                <NavContent />
                {!isAuthed && (
                  <Button variant="outline" onClick={() => { navigate("/login"); setOpen(false); }} data-testid="mobile-login-btn">
                    Entrar
                  </Button>
                )}
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
      <PendingBanner />
    </header>
  );
}
